<template>
  <div class="modal fade" id="projectUpload" tabindex="-1" role="dialog" aria-labelledby="projectUploadLabel">
    <div class="modal-dialog" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <button type="button" class="close" data-dismiss="modal" aria-label="Close" @click.prevent="modalReset">
            <span aria-hidden="true">&times;</span>
          </button>
          <h4 class="modal-title" id="projectUploadLabel">Upload A {{ $root.domainName }} Project</h4>
        </div>

        <div class="modal-body">
          <section id="images">
            <h6>
              <a tabindex="0" role="button" data-toggle="popover" data-trigger="focus" title="Project Images"
                data-content="Optimal image resolution is 1280px by 1024px.">
                Images <i class="fa fa-info-circle" aria-hidden="true"></i>
              </a>
            </h6>

            <div class="well text-center">
              <label for="file" class="btn btn-primary">
                <i :class="processing ? 'fa fa-spin fa-spinner' : 'fa fa-cloud-upload'"></i>
                Choose Images
              </label>

              <input id="file" class="file-input" name="file" type="file" @change="beginTheProjectUpload" accept="image/*"
                multiple ref="fileUpload" />

              <ul id="fileList" class="file-list">
                <li v-for="(image, index) in loadedImages" :key="index">
                  <span><i class="fa fa-picture-o" aria-hidden="true"></i> Project Image {{ index + 1 }}</span>
                  <a :href="image.image_url" target="_blank">
                    <i class="fa fa-eye" aria-hidden="true"></i>
                  </a>
                  <a href="#" @click.prevent="removeImage(image)">
                    <i class="fa fa-trash" aria-hidden="true"></i>
                  </a>
                </li>
              </ul>
            </div>
          </section>

          <section id="tags">
            <h6>
              <a tabindex="0" role="button" data-toggle="popover" data-trigger="focus" title="Project Tags"
                data-content="Tags are an important part of your project! The colors and hashtags you used are required items of your project submission.">Tags
                <i class="fa fa-info-circle" aria-hidden="true"></i></a>
            </h6>

            <label class="input-label">Colors Used (Required)</label>
            <Search @search="setTagData" :isUploadingProject="modalIsOpen" :setSearch="'project_colors'" />

            <label class="input-label">Hashtags (Required)</label>
            <Search @search="setTagData" :isUploadingProject="modalIsOpen" :setSearch="'hashtags'" />
          </section>

          <section id="content">
            <h6>
              <a tabindex="0" role="button" data-toggle="popover" data-trigger="focus" title="Project Title"
                data-content="(Optional) the name/title of your project.">
                Title <i class="fa fa-info-circle" aria-hidden="true"></i>
              </a>
            </h6>

            <div class="input-group">
              <span class="input-group-addon" id="project-title">
                <i :class="`fa fa-${projectTitle.length ? 'check' : 'exclamation'}-circle`" aria-hidden="true"></i>
              </span>
              <input type="text" class="form-control" placeholder="(Optional) Project Title"
                aria-describedby="project-title" v-model="projectTitle">
            </div>

            <h6>
              <a tabindex="0" role="button" data-toggle="popover" data-trigger="focus" title="Project Description"
                data-content="(Optional) add a brief description about your project.">
                Description <i class="fa fa-info-circle" aria-hidden="true"></i>
              </a>
            </h6>

            <div class="input-group">
              <span class="input-group-addon" id="project-description">
                <i :class="`fa fa-${projectStory.length ? 'check' : 'exclamation'}-circle`" aria-hidden="true"></i>
              </span>
              <input type="text" class="form-control" placeholder="(Optional) Project Description"
                aria-describedby="project-description" v-model="projectStory">
            </div>
          </section>
        </div>

        <div class="modal-footer row">
          <section id="submit">
            <div class="project-terms col-sm-12">
              <label for="projectNotes">
                <input type="checkbox" id="signUpForNewsLetter" v-model="signUpForNewsLetter" name="signUpForNewsLetter">
                Sign me up to receive email promotions
              </label>

              <label for="acceptTC">
                <input name="terms" id="acceptTC" type="checkbox" v-model="acceptTnC">
                I agree to <a href="/terms-and-policies#gallery-submissions" target="_blank">Terms &amp;
                  Conditions</a> (required to complete your submission)
              </label>
            </div>

            <div class="col-md-6 text-center">
              <button type="button" class="btn btn-block btn-black" data-dismiss="modal"
                @click.prevent="modalReset">Cancel</button>
            </div>

            <div class="col-md-6 text-center">
              <button type="button" class="btn btn-block btn-alt-outline" @click.prevent="submitTheProject"
                :disabled="hasUploadErrors">
                <i class="fa fa-spin fa-spinner" v-show="processing"></i>&nbsp;Submit
              </button>
            </div>
          </section>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import Search from '../global/Search.vue';

export default {
  /**
   * @file: ProjectUploadModal.vue
   * @description: displays and handles the modal for uploading a project to the Cerakote gallery.
   */

  name: 'ProjectUploadModal',

  components: {
    Search,
  },

  data() {
    return {
      modalIsOpen: false,
      selectedTags: [],
      selectedColorSkus: [],
      projectTitle: '',
      projectStory: '',
      acceptTnC: false,
      signUpForNewsLetter: false,
      uploadImg: {
        images: []
      },
      loadedImages: [],
      failedImages: [],
      projectId: '',
      processing: false,
      defaultErrorMessage: 'Please try reloading the page and trying again. If the problem continues please contact gallery@nicindustries.com so we can look into this further.',
    }
  },

  computed: {
    isLoggedIn() {
      return this.$root.isLoggedIn;
    },

    hasUploadErrors() {
      if (
        !this.loadedImages.length ||
        !this.selectedColorSkus.length ||
        !this.selectedTags.length ||
        !this.acceptTnC
      ) {
        return true;
      }

      return false;
    },

    // sets the project data for any POST/PATCH requests
    projectData() {
      return {
        color_product_ids: this.formatTags(this.selectedColorSkus) || [],
        description: this.projectStory,
        header: this.projectTitle,
        title: `${this.projectTitle} | ${this.$root.domainName}`,
        name: this.projectTitle,
        keywords: this.formatTags(this.selectedTags) || [],
      }
    }
  },

  mounted() {
    let vm = this;

    // toggle for use on the Search.vue component
    $("#projectUpload").on("shown.bs.modal", () => {
      vm.modalIsOpen = true;
    });

    // toggle for use on the Search.vue component
    $("#projectUpload").on("hide.bs.modal", () => {
      vm.modalIsOpen = false;
    });

    // enables bootstrap popover functionality
    $('[data-toggle="popover"]').popover();
  },

  methods: {
    /**
     * @function beginTheProjectUpload
     * @description begins the project upload process (step 1).
     * @param {Object} event - passes the click event element to be used for the uploadImages function.
    */
    async beginTheProjectUpload(event) {
      await this.initializeTheProject()
        .then(() => {
          this.uploadImages(event);
        })
    },

    /**
     * @function initializeTheProject
     * @description initializes the project, reserves the projectId for later use (step 2).
     */
    async initializeTheProject() {
      // prevent resetting the project ID if it already exists in Vue.
      if (this.projectId.length > 0) {
        return
      }

      await axios.post('/api/proxify/domains/cerakote/projects', this.projectData)
        .then(response => {
          // reserve and store the project id for later use on the media upload
          this.projectId = response.data.id;
        })
        .catch(error => {
          console.log(error);
        })
    },

    /**
     * @function uploadImages
     * @description uploads the images to the project (step 3). Much of this was borrowed from the old image upload function.
     * @param {Object} event - click event element.
     * TODO: look into using local storage to store the images and then uploading them all at once.
    */
    async uploadImages(event) {
      const dataTarget = event.dataTransfer || event.target;
      let files = dataTarget.files;
      let images = [];

      this.uploadImg.images.push(...Array.from(files));
      images = Array.from(files);

      for (const [index, image] of images.entries()) {
        this.processing = true;

        let formData = new FormData();

        formData.set('file', image);

        await axios.post(`/api/proxify/domains/cerakote/projects/${this.projectId}/media/upload`, formData)
          .then(response => {
            this.loadedImages.push(response.data);
          })
          .catch(error => {
            console.log(error);

            this.failedImages.push(image);

            this.$parent.$refs.notifications.add({
              delay: false,
              type: "error",
              icon: "exclamation-triangle",
              title: "Image file failed to upload",
              message: "Your image file is too large. Please choose a smaller file size and try again.",
            });
          })

        this.processing = false;
      }
    },

    /**
     * @function submitTheProject
     * @description submits the project (step 4) and resets the modal.
    */
    async submitTheProject() {
      if (this.processing) {
        return false;
      }

      this.processing = true;

      if (this.signUpForNewsLetter) {
        await this.newsLetterSubscribe();
      }

      if (this.uploadImg.images.length) {
        await this.finalizeProject();
        this.modalReset();
      }

      this.processing = false;
    },

    /**
     * @function setTagData
     * @description sets the hashtag and color data from Search.vue to use here for the project upload process.
     * @param {Object} queryParamData - unused, important to keep so that the tags param is mapped correctly.
     * @param {Array} tags - the selected tag objects from Search.vue.
     * @param {String} searchType - the type of search box.
    */
    setTagData(queryParamData, tags, searchType) {
      // Reset state array so tags are properly synced from the input
      if (searchType === 'hashtags') {
        this.selectedTags = [];
      } else if (searchType === 'project_colors') {
        this.selectedColorSkus = [];
      }
      
      tags.forEach(tag => {
        if (searchType === 'hashtags') {
          this.selectedTags.push(tag);
        }

        if (searchType === 'project_colors') {
          this.selectedColorSkus.push(tag);
        }
      });
    },

    /**
     * @function formatTags
     * @description formats the tags for the project upload process.
     * @param {Array} tags - the tags that were brought in with the setTagData function.
     * @returns {Array} - the formatted tags.
    */
    formatTags(tags) {
      const formattedTags = [];
      let tagType = '';

      tags.forEach(tag => {
        if (tag.type === 'hashtag') {
          tagType = 'hashtag';

          formattedTags.push(
            {
              id: tag.id,
              label: tag.label,
            }
          );
        }

        if (tag.type === 'project_colors') {
          tagType = 'color';

          formattedTags.push(
            tag.id,
          );
        }
      });

      // remove duplicates
      if (tagType === 'hashtag') {
        const formattedTagIds = formattedTags.map(orig => orig.id);
        let filteredTags = formattedTags.filter(({ id }, index) => !formattedTagIds.includes(id, index + 1));

        return filteredTags;
      }

      if (tagType === 'color') {
        let filteredTags = [...new Set(formattedTags)];

        return filteredTags;
      }
    },

    /**
     * @function modalReset
     * @description resets the modal data to its default state.
    */
    modalReset() {
      this.selectedTags = [];
      this.selectedColorSkus = [];
      this.projectTitle = '';
      this.projectStory = '';
      this.uploadImg.images = [];
      this.loadedImages = [];
      this.failedImages = [];
      this.processing = false;
      this.chosenProjectColors = [];
      this.acceptTnC = false;
      this.signUpForNewsLetter = false;
      this.projectId = '';
      this.$refs.fileUpload.value = null;
      $('#projectUpload').modal('hide');
    },

    async removeImage(image) {
      let imageIndex = this.loadedImages.indexOf(image);
      const imageId = image.id;

      this.processing = true;

      await axios.delete(`/api/proxify/domains/cerakote/projects/${this.projectId}/media/${imageId}`)
        .then(response => {
          this.loadedImages.splice(imageIndex, 1);
          this.uploadImg.images.splice(imageIndex, 1);
          this.$refs.fileUpload.value = null;
        })
        .catch(error => {
          console.log(error);

          this.$parent.$refs.notifications.add({
            delay: false,
            type: "error",
            icon: "exclamation-triangle",
            title: "Unable to remove image",
            message: this.defaultErrorMessage,
          });
        })

      this.processing = false;
    },

    async newsLetterSubscribe() {
      await axios.post(`/newsletter-subscribe-email/${this.$root.me.primary_email}`, {})
        .then(response => {
          this.$parent.$refs.notifications.add({
            delay: false,
            type: "success",
            icon: "check",
            title: "Congratulations!",
            message: `You've signed up to receive special promotions.`
          });
        })
        .catch(error => {
          console.log(error);
        });
    },

    async finalizeProject() {
      await axios.patch(`/api/proxify/domains/cerakote/projects/${this.projectId}`, this.projectData)
        .then(response => {
          this.$parent.$refs.notifications.add({
            delay: false,
            type: "success",
            icon: "check",
            title: "Project Submitted",
            message: `Your project was uploaded successfully, but must be approved before it is visible.`
          });
        })
        .catch(error => {
          console.log(error);

          this.$parent.$refs.notifications.add({
            delay: false,
            type: "error",
            icon: "exclamation-triangle",
            title: "Unable to upload project",
            message: this.defaultErrorMessage,
          });
        });
    },
  },
}
</script>

<style scoped lang="scss">
#projectUpload {

  label {
    font-weight: normal;
  }

  .modal-content {
    background-color: #fff;
    color: #333;
    border-radius: 6px;

    .modal-header {
      border-bottom: 1px solid #e5e5e5;
      padding: inherit;

      .close {
        color: #333;
        font-size: 25px;
        padding: 0;
      }
    }

    .modal-body {
      padding-bottom: 0;

      section {
        margin-bottom: 0.8rem;
      }

      .input-label {
        font-size: 1.1rem;
        text-transform: uppercase;
        color: #777;
      }

      .file-input {
        position: absolute;
        visibility: hidden;
      }

      ul.file-list {
        font-family: arial;
        list-style: none;
        padding: 0;
      }

      ul.file-list li {
        margin: 10px;
        outline: 1px solid #333;
        padding: 5px;
        border-radius: 3px;
        display: inline-block;
      }

      .form-group {
        margin-bottom: 0;
      }
    }

    .modal-footer {
      text-align: left;
      padding-top: 0;

      .project-terms {
        margin-bottom: 15px;
      }

      label {
        display: block;
      }
    }
  }
}
</style>